import { AfterViewInit, Component, HostBinding, NgZone, OnDestroy, OnInit } from '@angular/core';
import { COLOR_PICKER_MORE_TOOL } from '../../../../data/product/color-pickers.data';
import { HttpClient } from '@angular/common/http';
import { Location } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Clipboard } from '@angular/cdk/clipboard';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { extractColors } from 'extract-colors';
import * as Prism from 'prismjs';
import 'prismjs/components/prism-css';
import { addColorSwatches } from '../../../../util/code-swatches';
import { TOOLS, THEMES } from '../../../../data/product/product.data';
import { BLOG } from '../../../../data/blog/blog.data';
import { GRADIENT_ABOUT } from 'src/app/data/tool/tool-about.data';
import { Product } from '../../../../model/product/product.model';
import { URLS } from '../../../../data/navigation/navigation.data';
import { HEX_CODES } from '../../../../data/color/color.data';
import { CSS_SYNTAX_DEFAULT, CSS_SYNTAX_PREMIUM, CssSyntaxTheme } from '../../../../data/theme/css-syntax.data';
import { ColorService } from '../../../../service/color/color.service';
import { AnalyticsService } from '../../../../service/analytics/analytics.service';
import { SeoService } from '../../../../service/seo/seo.service';
import { GRADIENT_SCHEME_MINS, PaletteGenerationService } from '../../../../service/palette/palette-generation.service';
import { ProductThemeSelection, ThemeService } from '../../../../service/theme/theme.service';
import { PaletteSubtitle } from '../../../../component/palette-widget/palette-widget.component';

type GradientType = 'linear' | 'radial' | 'conic';

@Component( {
  selector: 'app-gradient-page',
  templateUrl: './gradient-page.component.html',
  styleUrls: [ './gradient-page.component.scss', '../tools-page/tools-page.component.scss', '../../plugin-pages/plugins-page/plugins-page.component.scss' ],
} )
export class GradientPageComponent implements OnInit, AfterViewInit, OnDestroy {

  TOOLS = TOOLS;
  URLS = URLS;

  // The palette (path param) + its source (query params), exactly like the
  // palette tool. The gradient is these colors spread along type/angle.
  palette: { value: string }[] = [];
  type: GradientType = 'linear';
  // Default lean matches the color tool's hero backdrop gradient (115deg).
  angle = 115;
  // Number of colours the next Initialize / From image action produces (3-10).
  count = 10;
  // The Range field mirrors the generated/loaded colour count, but only on the
  // first load — once set, user edits own it.
  private countSynced = false;
  // Explicit stop positions (0-100, ascending). Reset to even spacing whenever the
  // number of colours changes; the user can then drag each stop along the bar.
  positions: number[] = [];

  scheme = '';
  foundTheme: any = null;
  initializer?: { colorName: string; value: string };
  fullSeed = '';
  uploadedImg?: { src: string; id: string };

  // Aggregate channel stats across the palette (same section as the palette tool).
  channelStats: { rgb: any[]; hsl: any[]; hsv: any[]; cmyk: any[] } | null = null;

  // ----- Analysis sections (ported identically from the palette tool) -----
  relatedThemeList: { product: Product; category: string }[] = [];
  harmonyResult: any[] = [];
  harmonyScores: { name: string; description: string; score: number; counts: number[] }[] = [];
  shadeTintTone: { title: string; rows: string[][] }[] = [];
  temperatureVariants: { title: string; rows: string[][] }[] = [];
  analogous: number[] = [];
  complementaries: number[] = [];
  triadics: number[] = [];
  temperatures: string[] = [];
  protanomalyColors: string[] = [];
  deuteranomalyColors: string[] = [];
  tritanomalyColors: string[] = [];
  protanopiaColors: string[] = [];
  deuteranopiaColors: string[] = [];
  tritanopiaColors: string[] = [];
  achromatomalyColors: string[] = [];
  achromatopsiaColors: string[] = [];
  copiedKey = '';
  private copiedKeyTimer: ReturnType<typeof setTimeout> | null = null;
  // Three random blog posts, reshuffled per load (same as the palette tool).
  blogCards = [ ...BLOG ].sort( () => Math.random() - 0.5 ).slice( 0, 3 );
  /** the How It Works closer under the blog (the server prints the same four questions as FAQPage) */
  readonly about = GRADIENT_ABOUT;

  copied = false;
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  // Code-window syntax colours, following the active IDE-skin theme exactly like
  // the palette / color tool pages.
  private themeSub?: Subscription;
  private darkLightSub?: Subscription;
  private currentSelection: ProductThemeSelection | null = null;
  private darkLight = 'auto';
  editorBg = '';
  editorFg = '';
  synComment = '';
  synSelector = '';
  synProperty = '';
  synValue = '';
  synKeyword = '';
  synPunctuation = '';
  // Cache the Prism highlight so it only re-runs when the CSS text changes.
  private cssCache = '';
  private cssHtmlCache: SafeHtml = '';

  pageSections: { key: string; label: string }[] = [
    { key: 'preview', label: 'Preview' },
    { key: 'channels', label: 'Color Channels' },
    { key: 'harmony', label: 'Harmony' },
    { key: 'shades', label: 'Tints & Shades' },
    { key: 'temperature', label: 'Temperature' },
    { key: 'vision', label: 'Color Vision' },
    { key: 'more', label: 'More Color Tools' },
    { key: 'themes', label: 'Related Themes' },
    { key: 'blog', label: 'From The Blog' },
    { key: 'about', label: 'How It Works' },
    { key: 'reviews', label: 'Reviews' },
  ];
  /** More Color Tools: the Color Picker */
  readonly colorPicker = COLOR_PICKER_MORE_TOOL;
  readonly moreToolsSource = 'gradient-generator';
  activeSection = 'top';
  private sectionObserver?: IntersectionObserver;

  // Picker edits fire rapidly; debounce the URL write so history isn't spammed.
  // `editPending` lets a non-edit action (Initialize, drag, add/remove, image)
  // cancel a still-debouncing edit so its late write can't clobber the new source.
  private editSubject = new Subject<void>();
  private editPending = false;
  // Angle drags fire rapidly; debounce the URL write so history isn't spammed.
  private settingsSubject = new Subject<void>();
  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private colorService: ColorService,
    private paletteGen: PaletteGenerationService,
    private analyticsService: AnalyticsService,
    private seoService: SeoService,
    private zone: NgZone,
    private http: HttpClient,
    private themeService: ThemeService,
    private sanitizer: DomSanitizer,
    private clipboard: Clipboard,
  ) {
  }

  ngOnInit(): void {
    this.seoService.setMetaData( {
      title: 'CSS Gradient Generator - Free Linear, Radial & Conic Gradient Maker | Codigrate',
      description: 'Free CSS gradient generator by Codigrate. Build linear, radial and conic gradients from your own color stops, set the angle, and copy ready-to-use CSS.',
      keywords: [ 'css gradient generator', 'gradient generator', 'linear gradient', 'radial gradient', 'conic gradient', 'css background', 'color stops', 'vibe coding', 'codigrate' ],
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );

    // Code-window scheme follows the active IDE-skin theme (same as the palette
    // and color tools); recompute when the theme or the dark/light mode changes.
    this.themeSub = this.themeService.productTheme.subscribe( selection => {
      this.currentSelection = selection;
      this.applyCodeScheme();
    } );
    this.darkLightSub = this.themeService.darkLightSetting.subscribe( mode => {
      this.darkLight = mode;
      if ( !this.currentSelection ) {
        this.applyCodeScheme();
      }
    } );

    // Path param = the colors. Blank visit seeds a random palette and reflects it
    // in the URL (so each fresh visit re-randomizes), exactly like the palette tool.
    this.subs.push( this.route.params.subscribe( params => {
      if ( params.gradient ) {
        const hexes = String( params.gradient ).split( '-' );
        if ( hexes.length < 2 || hexes.length > 10 || hexes.some( h => !this.colorService.isValidHexCode( h ) ) ) {
          this.router.navigate( [ URLS.notFound ] );
          return;
        }
        // The blank visit populates the palette synchronously and then redirects
        // to this very URL, which fires here again with the SAME colours. Skip the
        // rebuild in that case so the gradient doesn't render (flash) a second time.
        const same = this.palette.length === hexes.length
          && this.palette.every( ( p, i ) => p.value.slice( 1 ).toLowerCase() === hexes[ i ].toLowerCase() );
        if ( !same ) {
          this.palette = hexes.map( h => ( { value: '#' + h.toLowerCase() } ) );
          this.syncPositions();
          this.buildChannelStats();
          // On the very first load (e.g. a shared /<hexes> link), reflect the
          // colour count in the Range field. Guarded so later edits / drags don't
          // keep rewriting it.
          if ( !this.countSynced ) {
            this.count = this.palette.length;
            this.countSynced = true;
          }
        }
        // Custom stop positions from the URL override the even-spacing default.
        const urlPos = this.parsePositions( this.route.snapshot.queryParams.position );
        if ( urlPos ) {
          this.positions = urlPos;
        }
      } else {
        const start = this.paletteGen.randomStart( GRADIENT_SCHEME_MINS );
        if ( start.hexes && start.hexes.length >= 2 ) {
          const qp = this.startQueryParams( start );
          // Reflect the random count in the Range field so it matches the stops shown.
          this.count = start.hexes.length;
          this.countSynced = true;
          // A random landing also gets a random gradient type + angle.
          this.type = this.randomType();
          this.angle = this.randomAngle();
          if ( this.palette.length === 0 ) {
            // FRESH load (home -> gradient, or a direct visit): populate the palette
            // + source synchronously so the card never flashes an empty gradient
            // while the URL-reflecting navigation is still in flight.
            this.palette = start.hexes.map( h => ( { value: '#' + h.replace( '#', '' ).toLowerCase() } ) );
            this.syncPositions();
            this.applySourceParams( qp );
            this.buildChannelStats();
          }
          // Re-visit (clicking the nav link while already on the page): DON'T render
          // the new gradient here at the bare URL. Leave the current gradient on
          // screen and let the redirect below produce a SINGLE clean transition at
          // the valued URL — rendering it here too caused a double paint (flick /
          // duplicate). Replace the bare entry so Back skips it.
          // Render the random start IN PLACE: the router state moves to the
          // valued route but the address bar stays on the bare /tools/gradient,
          // so crawlers never see a JS redirect off the landing page.
          this.navigateTo( start.hexes, qp, true, true );
        } else {
          // Guard: generation yielded nothing — set a default directly rather
          // than navigating to the empty base again (which would loop).
          this.palette = [ { value: '#00baff' }, { value: '#ba00ff' } ];
          this.syncPositions();
          this.buildChannelStats();
        }
      }
      this.refreshSeo();
    } ) );

    // Query params = the subtitle source (theme / harmony seed / scheme / seed).
    this.subs.push( this.route.queryParams.subscribe( params => {
      // On a blank /gradient visit this fires once with EMPTY params before the
      // random-start redirect lands. The params subscription has already set the
      // source synchronously, so applying empty params here would wipe it and make
      // the subtitle flash in late. Skip until we are on a real (path-param'd) URL.
      if ( !this.route.snapshot.params.gradient ) {
        return;
      }
      this.applySourceParams( params );
      // Gradient type / angle from the URL (so a shared link reproduces them).
      // Only overwrite when present so toggling to radial (which drops the angle
      // from the URL) and back keeps the user's chosen angle.
      if ( params.type === 'linear' || params.type === 'radial' || params.type === 'conic' ) {
        this.type = params.type;
      }
      if ( params.angle != null && params.angle !== '' ) {
        const a = parseInt( params.angle, 10 );
        if ( !isNaN( a ) ) {
          this.angle = Math.max( 0, Math.min( 360, a ) );
        }
      }
      // A genuine source from the URL means the upload (a local-only source) is stale.
      if ( params.theme || params.scheme ) {
        this.uploadedImg = undefined;
      }
      this.refreshSeo();
    } ) );

    this.subs.push( this.editSubject.pipe( debounceTime( 600 ) ).subscribe( () => {
      if ( !this.editPending ) {
        return;
      }
      this.editPending = false;
      this.navigateTo( this.palette.map( p => p.value ), {} );
    } ) );

    this.subs.push( this.settingsSubject.pipe( debounceTime( 350 ) ).subscribe( () => this.persistSettings() ) );
  }

  ngAfterViewInit(): void {
    if ( typeof IntersectionObserver === 'undefined' ) {
      return;
    }
    this.sectionObserver = new IntersectionObserver( entries => {
      let changed = false;
      for ( const entry of entries ) {
        if ( entry.isIntersecting ) {
          const sec = ( entry.target as HTMLElement ).dataset[ 'section' ];
          if ( sec && sec !== this.activeSection ) {
            this.zone.run( () => ( this.activeSection = sec ) );
            changed = true;
          }
        }
      }
      if ( changed ) {
        this.scrollActivePillToStart();
      }
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 } );
    setTimeout( () => {
      document.querySelectorAll( '[data-section]' ).forEach( el => this.sectionObserver!.observe( el ) );
    } );
  }

  ngOnDestroy(): void {
    this.sectionObserver?.disconnect();
    this.themeSub?.unsubscribe();
    this.darkLightSub?.unsubscribe();
    this.subs.forEach( s => s.unsubscribe() );
  }

  // ----- Code window (CSS, syntax-highlighted to the active theme) -----

  // The gradient declaration as a CSS rule shown in the code window: one colour
  // stop per line (after each comma) so the swatch util can prepend a colour chip
  // at the start of every stop's line.
  cssRule(): string {
    const colors = this.palette.map( p => p.value ).filter( Boolean );
    if ( colors.length === 0 ) {
      return `.gradient {\n  background: transparent;\n}`;
    }
    if ( colors.length === 1 ) {
      return `.gradient {\n  background: ${ colors[ 0 ] };\n}`;
    }
    const n = colors.length;
    const usePos = this.positions.length === n;
    const stops = colors.map( ( c, i ) => {
      const pct = usePos ? this.positions[ i ] : ( i / ( n - 1 ) * 100 );
      return `    ${ c } ${ pct.toFixed( 2 ) }%`;
    } ).join( ',\n' );
    let fn: string;
    if ( this.type === 'radial' ) {
      fn = `radial-gradient(circle,\n${ stops }\n  )`;
    } else if ( this.type === 'conic' ) {
      fn = `conic-gradient(from ${ this.angle }deg,\n${ stops }\n  )`;
    } else {
      fn = `linear-gradient(${ this.angle }deg,\n${ stops }\n  )`;
    }
    return `.gradient {\n  background: ${ fn };\n}`;
  }

  // Prism-highlighted + colour-swatched HTML for the code window. Cached so the
  // highlight only re-runs when the CSS text actually changes.
  get gradientCssHtml(): SafeHtml {
    const css = this.cssRule();
    if ( css !== this.cssCache ) {
      this.cssCache = css;
      this.cssHtmlCache = this.sanitizer.bypassSecurityTrustHtml(
        addColorSwatches( Prism.highlight( css, Prism.languages.css, 'css' ) ),
      );
    }
    return this.cssHtmlCache;
  }

  // Pick the code-window scheme exactly like the palette / color tool: a premium
  // theme uses bundled colours, a free theme its public palette.json, no skin the
  // default.
  private applyCodeScheme(): void {
    const sel = this.currentSelection;
    if ( sel?.key && CSS_SYNTAX_PREMIUM[ sel.key ] ) {
      this.applyCssSyntax( CSS_SYNTAX_PREMIUM[ sel.key ] );
    } else if ( sel?.key && sel?.category ) {
      this.loadThemePalette( sel.category, sel.key );
    } else {
      this.loadDefaultPalette();
    }
  }

  private loadThemePalette( category: string, key: string ): void {
    const url = `assets/themes/${ category }/${ key }-theme/${ key }.palette.json`;
    this.http.get<{ tokens?: any }>( url ).subscribe( {
      next: palette => this.applyThemePalette( palette?.tokens ),
      error: () => this.applyCssSyntax( CSS_SYNTAX_DEFAULT ),
    } );
  }

  private loadDefaultPalette(): void {
    this.applyCssSyntax( CSS_SYNTAX_DEFAULT );
    const mode = this.isDarkMode() ? 'dark' : 'light';
    this.http.get<{ tokens?: any }>( `assets/themes/default/default-${ mode }.palette.json` ).subscribe( {
      next: palette => {
        if ( palette?.tokens ) {
          this.applyThemePalette( palette.tokens );
        }
      },
      error: () => { /* keep the bundled emergency default */ },
    } );
  }

  private isDarkMode(): boolean {
    if ( this.darkLight === 'dark' ) { return true; }
    if ( this.darkLight === 'light' ) { return false; }
    return typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia( '(prefers-color-scheme: dark)' ).matches;
  }

  private applyCssSyntax( t: CssSyntaxTheme ): void {
    this.editorBg = t.editorBackground;
    this.editorFg = t.editorForeground;
    this.synComment = t.comment;
    this.synSelector = t.selector;
    this.synProperty = t.property;
    this.synValue = t.value;
    this.synKeyword = t.keyword;
    this.synPunctuation = t.punctuation;
  }

  private applyThemePalette( tokens: any ): void {
    if ( !tokens ) {
      this.applyCssSyntax( CSS_SYNTAX_DEFAULT );
      return;
    }
    const css = tokens.css ?? {};
    const semantic = tokens.semantic ?? {};
    this.editorBg = tokens.editor?.background ?? tokens.interface?.editorBackground ?? '';
    this.editorFg = tokens.editor?.foreground ?? semantic.plainText ?? '';
    this.synComment = css.comment ?? semantic.comment ?? '';
    this.synSelector = css.selector ?? semantic.selector ?? semantic.tag ?? '';
    this.synProperty = css.property ?? semantic.property ?? semantic.field ?? '';
    this.synValue = css.number ?? css.string ?? semantic.number ?? semantic.string ?? semantic.attributeValue ?? '';
    this.synKeyword = css.keyword ?? semantic.keyword ?? '';
    this.synPunctuation = css.punctuation ?? semantic.punctuation ?? semantic.operator ?? '';
  }

  // ----- Navigation helpers -----

  private startQueryParams( start: { theme?: any; scheme?: string; initializer?: string; seed?: string } ): any {
    if ( start.theme ) {
      return { theme: start.theme.key };
    }
    const qp: any = { scheme: start.scheme };
    if ( start.initializer ) { qp.initializer = start.initializer; }
    if ( start.seed ) { qp.seed = start.seed; }
    return qp;
  }

  private navigateTo( hexes: string[], queryParams: any, replace = false, skipLocation = false ): void {
    // Any explicit navigation supersedes a still-debouncing edit write.
    this.editPending = false;
    const params = hexes.map( h => h.replace( '#', '' ) ).join( '-' );
    this.router.navigate( [ URLS.tools.gradient + '/' + params ],
      { queryParams: this.withGradientQuery( queryParams ), replaceUrl: replace, skipLocationChange: skipLocation, state: { scrollToTop: false } } );
  }

  // Map the source query params (theme / harmony seed / scheme / full seed) onto
  // the subtitle state. Shared by the queryParams subscription and the blank-visit
  // start, so the source line is correct on the very first render (no flash).
  private applySourceParams( params: any ): void {
    this.foundTheme = params.theme
      ? THEMES.map( l => l.products ).reduce( ( a: any[], b: any[] ) => a.concat( b ), [] ).find( ( p: any ) => p.key === params.theme )
      : null;
    this.initializer = params.initializer
      ? {
        colorName: String( params.initializer ).replace( /-/g, ' ' ),
        value: HEX_CODES[ params.initializer ] ? '#' + HEX_CODES[ params.initializer ] : '',
      }
      : undefined;
    this.scheme = params.scheme || '';
    this.fullSeed = params.seed || '';
  }

  // The gradient type / angle ride along in the URL on every navigation so the
  // gradient is fully reproducible from the link (the angle only when it applies).
  private get usesAngle(): boolean {
    return this.type !== 'radial';
  }

  private withGradientQuery( source: any ): any {
    const qp: any = { ...source, type: this.type };
    if ( this.usesAngle ) {
      qp.angle = this.angle;
    }
    // Custom stop positions ride in the URL too (coolors-style) so a dragged
    // gradient is fully reproducible. Even spacing is the implicit default, so
    // it is omitted to keep generated links clean.
    if ( !this.positionsAreDefault() ) {
      qp.position = this.positions.map( p => +p.toFixed( 2 ) ).join( ',' );
    }
    return qp;
  }

  // True when the stops sit at plain even spacing (so positions add nothing to
  // the URL). Any user drag / numeric edit makes this false and writes them out.
  private positionsAreDefault(): boolean {
    const n = this.positions.length;
    if ( n !== this.palette.length ) {
      return true;
    }
    return this.positions.every( ( p, i ) => {
      const even = n > 1 ? ( i / ( n - 1 ) ) * 100 : 0;
      return Math.abs( p - even ) < 0.01;
    } );
  }

  // Parse a `position=` query value into an ascending, clamped array that lines
  // up with the current palette length; null if absent or malformed.
  private parsePositions( raw: any ): number[] | null {
    if ( raw == null || raw === '' ) {
      return null;
    }
    const arr = String( raw ).split( ',' ).map( s => parseFloat( s ) );
    if ( arr.length !== this.palette.length || arr.some( n => isNaN( n ) ) ) {
      return null;
    }
    let prev = 0;
    return arr.map( ( v, i ) => {
      const clamped = Math.max( prev, Math.min( 100, v ) );
      prev = i < arr.length - 1 ? clamped : prev;
      return clamped;
    } );
  }

  // Re-write only the type / angle into the URL, keeping the current palette
  // (path) and source query params untouched. `replaceUrl` keeps slider drags
  // and type toggles out of the browser history.
  private persistSettings(): void {
    const source = { ...this.route.snapshot.queryParams };
    delete source.type;
    delete source.angle;
    this.router.navigate( [], {
      relativeTo: this.route,
      queryParams: this.withGradientQuery( source ),
      replaceUrl: true,
      state: { scrollToTop: false },
    } );
  }

  // ----- Widget intents (mirror the palette tool) -----

  // A random gradient type, so each random / generate also varies linear / radial
  // / conic (not just the colours).
  private randomType(): GradientType {
    const types: GradientType[] = [ 'linear', 'radial', 'conic' ];
    return types[ Math.floor( Math.random() * types.length ) ];
  }

  // A random angle (0-359deg) so the lean varies too.
  private randomAngle(): number {
    return Math.floor( Math.random() * 360 );
  }

  onGenerate( scheme: string ): void {
    // A fresh generation resets stop positions to even spacing and picks a random
    // gradient type + angle too.
    this.positions = [];
    this.type = this.randomType();
    this.angle = this.randomAngle();
    if ( scheme === 'theme' || scheme.startsWith( 'theme:' ) ) {
      // 'theme:<key>' = the theme picked in the generator (owner 2026-10-12), 'theme' = any theme
      const t = scheme.startsWith( 'theme:' ) ? this.paletteGen.themeByKey( scheme.slice( 6 ) ) : this.paletteGen.randomTheme( this.foundTheme?.key );
      if ( !t.hexes.length ) {
        return;
      }
      this.navigateTo( t.hexes.slice( 0, this.count ), { theme: t.theme?.key } );
      this.analyticsService.logEvent( 'Gradient Initialized', { source: 'theme' } );
      return;
    }
    if ( scheme === 'featured' ) {
      // A random curated two-stop gradient from the open-data set; its name is
      // recovered from the colours by the subtitle (kind: 'named').
      const f = this.paletteGen.featuredGradient();
      if ( !f.hexes.length ) {
        return;
      }
      this.navigateTo( f.hexes, { scheme: 'featured' } );
      this.analyticsService.logEvent( 'Gradient Initialized', { source: 'featured' } );
      return;
    }
    const r = this.paletteGen.build( scheme, this.count );
    if ( !r.hexes.length ) {
      return;
    }
    const qp: any = { scheme: r.scheme };
    if ( r.initializer ) { qp.initializer = r.initializer; }
    if ( r.seed ) { qp.seed = r.seed; }
    this.navigateTo( r.hexes, qp );
    this.analyticsService.logEvent( 'Gradient Initialized', { source: r.scheme || scheme } );
  }

  onEditColor( change: { index: number; value: string } ): void {
    const item = this.palette[ change.index ];
    if ( !item ) {
      return;
    }
    item.value = change.value;
    this.buildChannelStats();
    this.editPending = true;
    this.editSubject.next();
  }

  drop( event: CdkDragDrop<unknown> ): void {
    moveItemInArray( this.palette, event.previousIndex, event.currentIndex );
    // These handlers mutate this.palette IN PLACE before navigating, so the params
    // subscription's "same colours" guard skips its rebuild. Rebuild the analysis
    // here so the bars never go stale (the edit handler does the same).
    this.buildChannelStats();
    this.navigateTo( this.palette.map( p => p.value ), {} );
  }

  addColor(): void {
    if ( this.palette.length >= 10 ) {
      return;
    }
    this.palette.push( { value: this.colorService.generateRandomHexColor() } );
    // The "+" tile has no target position, so fall back to even spacing.
    this.positions = [];
    this.buildChannelStats();
    this.navigateTo( this.palette.map( p => p.value ), {} );
  }

  /** the widget's reverse button: the stops in the opposite order, a custom stop's place mirrored */
  reverseStops(): void {
    if ( this.palette.length < 2 ) return;
    this.palette.reverse();
    if ( this.positions.length === this.palette.length ) {
      this.positions = this.positions.map( p => +( 100 - p ).toFixed( 2 ) ).reverse();
    }
    this.buildChannelStats();
    this.navigateTo( this.palette.map( p => p.value ), {} );
    this.analyticsService.logEvent( 'Gradient Reversed', { count: this.palette.length } );
  }

  removeColor( index: number ): void {
    if ( this.palette.length <= 2 ) {
      return;
    }
    this.palette.splice( index, 1 );
    // Keep the surviving stops where the user placed them.
    this.positions.splice( index, 1 );
    this.buildChannelStats();
    this.navigateTo( this.palette.map( p => p.value ), {} );
  }

  onTypeChange( type: GradientType ): void {
    this.type = type;
    this.persistSettings();
    this.analyticsService.logEvent( 'Gradient Type Changed', { type } );
  }

  onAngleChange( angle: number ): void {
    this.angle = angle;
    this.settingsSubject.next();
  }

  handleImageUpload( event: any ): void {
    const file = event.target.files[ 0 ];
    if ( !file ) {
      return;
    }
    const reader = new FileReader();
    reader.onload = ( e: any ) => {
      const img = new Image();
      img.src = e.target.result;
      img.id = file.name;
      img.onload = async () => {
        try {
          let colors = await extractColors( img.src, {
            pixels: 500, distance: 0.1, saturationDistance: 0, lightnessDistance: 0, hueDistance: 0,
          } );
          // extract-colors can settle OUTSIDE Angular's zone, which leaves the
          // palette + every analysis bar (harmony / tints / temperature / vision)
          // stale until the next interaction. Run the state update + navigation
          // inside the zone so change detection always fires.
          this.zone.run( () => {
            colors.sort( ( a, b ) => b.area - a.area );
            if ( colors.length > this.count ) {
              colors = colors.slice( 0, this.count );
            }
            while ( colors.length < 3 ) {
              colors.push( colors[ 0 ] );
            }
            const palette = colors.map( c => ( { value: c.hex } ) );
            this.colorService.sortByHue( palette );
            // Fresh image palette resets stop positions to even spacing.
            this.positions = [];

            const canvas = document.createElement( 'canvas' );
            // The image doubles as the hero backdrop (shown sharp, full width), so
            // keep it large enough to stay crisp instead of the old 240px thumbnail.
            const maxW = 1280;
            const scale = Math.min( 1, maxW / img.width );
            canvas.width = Math.round( img.width * scale );
            canvas.height = Math.round( img.height * scale );
            const ctx = canvas.getContext( '2d' );
            if ( ctx ) {
              ctx.drawImage( img, 0, 0, canvas.width, canvas.height );
              this.uploadedImg = { src: canvas.toDataURL( 'image/jpeg', 0.85 ), id: file.name };
            }
            // Image source is local (not in the URL); just reflect the colors.
            this.navigateTo( palette.map( p => p.value ), {} );
            this.analyticsService.logEvent( 'Gradient Initialized', { source: 'image' } );
          } );
        } catch ( error ) {
          console.error( 'Error reading image palette:', error );
        }
      };
    };
    reader.readAsDataURL( file );
  }

  // ----- Subtitle (mirror the palette tool) -----

  private themeHref( theme: any ): string {
    for ( const line of THEMES ) {
      if ( line.products.some( ( p: any ) => p.key === theme?.key ) ) {
        return `${ URLS.theme }?product=${ theme.key }&category=${ ( line.name || '' ).toLowerCase() }`;
      }
    }
    return '';
  }

  private colorHref( hex: string ): string {
    const h = ( hex || '' ).replace( '#', '' );
    return h ? `${ URLS.tools.color }/${ h }` : '';
  }

  private get themeName(): string {
    const name = ( this.foundTheme?.name || '' ).trim().replace( /\s*theme\s*$/i, '' );
    return name ? `${ name } Theme` : '';
  }

  private get initializerName(): string {
    return ( this.initializer?.colorName || '' ).replace( /\b\w/g, c => c.toUpperCase() );
  }

  // The generator the current palette came from, so the Initialize dropdown can
  // reflect it on landing (image / theme / a harmony scheme); '' for a custom
  // palette with no recognised source.
  get currentGenerator(): string {
    if ( this.uploadedImg ) { return 'image'; }
    if ( this.foundTheme ) { return 'theme'; }
    return this.scheme || '';
  }

  /** over every section card, the generator and the source (owner 2026-10-12): "Analogous · Istanbul Theme",
   *  "Codigrate Themes · Tallinn Theme", "Full Random · seed-..." (a middle dot, the site uses no dashes) */
  get sourceLabel(): string {
    const s = this.subtitle;
    switch ( s.kind ) {
      case 'theme': return 'Codigrate Themes · ' + ( s.themeName || 'Theme' );
      case 'harmony': return this.schemeTitle + ' · ' + ( s.name || '' );
      case 'named': return 'Featured · ' + ( s.name || '' );
      case 'image': return 'From Image · ' + ( s.imageName || 'Uploaded Image' );
      case 'full': return 'Full Random · ' + ( s.seed ? 'seed-' + s.seed : 'Random' );
      default: return 'Custom · ' + ( s.seed ? 'seed-' + s.seed : 'Gradient' );
    }
  }

  // the scheme in Title Case for the hero title ("Split Complementary Gradient", owner 2026-10-12)
  get schemeTitle(): string {
    return ( this.subtitle.scheme || '' ).trim().replace( /\b\w/g, ch => ch.toUpperCase() );
  }

  /** the page's top band (page-band.scss): the gradient's colours side by side, each a little, fading down to
   *  the page (owner 2026-10-12, as on the palette tool) */
  @HostBinding( 'style.--band' ) get band(): string {
    const first = this.palette.find( p => /^#[0-9a-f]{6}$/i.test( p.value || '' ) )?.value;
    return `color-mix(in oklab, ${ first || 'var(--theme-color)' } 55%, var(--background-color))`;
  }

  @HostBinding( 'style.--band-row' ) get bandRow(): string {
    const colors = this.palette.map( p => p.value ).filter( c => /^#[0-9a-f]{6}$/i.test( c || '' ) );
    if ( colors.length < 2 ) {
      return `linear-gradient(180deg, ${ this.band } 0%, var(--background-color) 100%)`;
    }
    const stops = colors.map( c => `color-mix(in oklab, ${ c } 55%, var(--background-color))` );
    return `linear-gradient(90deg, ${ stops.join( ', ' ) })`;
  }

  get subtitle(): PaletteSubtitle {
    if ( this.uploadedImg ) {
      return { kind: 'image', imageSrc: this.uploadedImg.src, imageName: this.uploadedImg.id };
    }
    if ( this.foundTheme ) {
      return { kind: 'theme', themeName: this.themeName, logo: this.foundTheme.logo, themeHref: this.themeHref( this.foundTheme ) };
    }
    const sourced = [ 'complementary', 'split complementary', 'triadic', 'tetradic', 'analogous', 'monochrome', 'warm', 'cool' ];
    if ( sourced.includes( this.scheme ) && this.initializer ) {
      return { kind: 'harmony', scheme: this.scheme, name: this.initializerName, color: this.initializer.value, colorHref: this.colorHref( this.initializer.value ) };
    }
    if ( this.scheme === 'full' ) {
      return { kind: 'full', seed: this.fullSeed || '' };
    }
    // A Featured gradient: recover its curated name from the colours themselves,
    // and show the colours blended as the source swatch.
    if ( this.scheme === 'featured' ) {
      const cols = this.palette.map( p => p.value );
      const name = this.colorService.getColorSetName( cols );
      if ( name ) {
        return { kind: 'named', name, gradient: `linear-gradient(90deg, ${ cols.join( ', ' ) })` };
      }
    }
    // Anything else (the user edited the palette / a bare-hex link) is a custom
    // theme; give it a stable seed fingerprint so it still reads as a Seed source.
    return { kind: 'custom', seed: this.palette.length ? this.paletteGen.paletteSeed( this.palette.map( p => p.value ) ) : '' };
  }

  // Reflect the produced gradient + its source in the document head, so each
  // generated / shared gradient (above all the named Featured ones) carries a
  // descriptive, indexable title + description instead of the static page one.
  // Driven off the same subtitle state shown in the widget header.
  private refreshSeo(): void {
    // Blank landings render at the bare /tools/gradient URL (skipLocationChange);
    // its static landing meta must survive, so only real param URLs get the
    // per-gradient head.
    if ( !this.location.path().split( '?' )[ 0 ].startsWith( URLS.tools.gradient + '/' ) ) {
      return;
    }
    const sub = this.subtitle;
    let title = 'CSS Gradient Generator - Linear, Radial & Conic | Codigrate';
    let lead = 'Free CSS gradient generator by Codigrate.';
    const extraKeyword: string[] = [];
    switch ( sub.kind ) {
      case 'named':
        title = `${ sub.name } Gradient - CSS ${ this.typeLabel } Gradient | Codigrate`;
        lead = `${ sub.name }, a featured CSS ${ this.type } gradient.`;
        extraKeyword.push( ( sub.name || '' ).toLowerCase(), `${ ( sub.name || '' ).toLowerCase() } gradient` );
        break;
      case 'theme':
        title = `${ sub.themeName } Gradient - CSS Gradient Generator | Codigrate`;
        lead = `A CSS gradient generated from the ${ sub.themeName }.`;
        extraKeyword.push( ( sub.themeName || '' ).toLowerCase() );
        break;
      case 'harmony':
        title = sub.name
          ? `${ this.cap( sub.scheme || '' ) } CSS Gradient from ${ sub.name } | Codigrate`
          : `${ this.cap( sub.scheme || '' ) } CSS Gradient | Codigrate`;
        lead = `Generated a ${ sub.scheme } gradient${ sub.name ? ' from ' + sub.name : '' }.`;
        extraKeyword.push( `${ sub.scheme } gradient`, ...( sub.name ? [ sub.name.toLowerCase() ] : [] ) );
        break;
      case 'full':
        title = sub.seed ? `Random CSS Gradient from seed-${ sub.seed } | Codigrate` : 'Random CSS Gradient | Codigrate';
        lead = `Generated a random ${ this.type } gradient${ sub.seed ? ' from seed-' + sub.seed : '' }.`;
        extraKeyword.push( 'random gradient', 'random gradient generator' );
        break;
      case 'image':
        title = `CSS Gradient from an Image - ${ this.typeLabel } | Codigrate`;
        lead = `A CSS ${ this.type } gradient generated from the colours of an uploaded image.`;
        extraKeyword.push( 'gradient from image', 'image to gradient', 'image color extractor' );
        break;
      case 'custom':
        title = sub.seed ? `Custom CSS Gradient from seed-${ sub.seed } | Codigrate` : 'Custom CSS Gradient | Codigrate';
        lead = `A custom CSS ${ this.type } gradient${ sub.seed ? ' from seed-' + sub.seed : '' }.`;
        extraKeyword.push( 'custom gradient' );
        break;
    }
    const description = `${ lead } Copy the ready-to-use linear, radial and conic CSS, tweak the stops and angle, free from Codigrate.`;
    this.seoService.setMetaData( {
      title,
      description,
      keywords: [ 'css gradient generator', 'gradient generator', 'linear gradient', 'radial gradient', 'conic gradient', ...extraKeyword, 'codigrate' ].filter( Boolean ),
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );
  }

  private get typeLabel(): string {
    return this.cap( this.type );
  }

  private cap( s: string ): string {
    return ( s || '' ).replace( /\b\w/g, c => c.toUpperCase() );
  }

  // Reset positions to even spacing only when the colour COUNT changed, so a
  // colour edit or reorder keeps any custom stop positions the user set.
  private syncPositions(): void {
    const n = this.palette.length;
    if ( this.positions.length === n ) {
      return;
    }
    this.positions = this.palette.map( ( _, i ) => ( n > 1 ? +( i / ( n - 1 ) * 100 ).toFixed( 2 ) : 0 ) );
  }

  // A stop's % can't pass its neighbours (left < right), keeping them ascending.
  onPositionChange( change: { index: number; value: number } ): void {
    const i = change.index;
    const lo = i > 0 ? this.positions[ i - 1 ] : 0;
    const hi = i < this.positions.length - 1 ? this.positions[ i + 1 ] : 100;
    this.positions[ i ] = Math.max( lo, Math.min( hi, change.value ) );
    // Persist the dragged / typed stop position into the URL (debounced).
    this.settingsSubject.next();
  }

  // Insert a stop at a clicked position on the bar, with a colour interpolated
  // between its new neighbours, keeping both arrays index-aligned and ascending.
  insertStop( at: { position: number } ): void {
    if ( this.palette.length >= 10 ) {
      return;
    }
    const pos = Math.max( 0, Math.min( 100, at.position ) );
    // Find the gap this position falls into.
    let idx = this.positions.findIndex( p => p > pos );
    if ( idx === -1 ) {
      idx = this.positions.length;
    }
    const leftHex = this.palette[ idx - 1 ]?.value ?? this.palette[ 0 ]?.value ?? '#000000';
    const rightHex = this.palette[ idx ]?.value ?? leftHex;
    const leftPos = idx > 0 ? this.positions[ idx - 1 ] : 0;
    const rightPos = idx < this.positions.length ? this.positions[ idx ] : 100;
    const t = rightPos > leftPos ? ( pos - leftPos ) / ( rightPos - leftPos ) : 0.5;
    const value = this.colorService.interpolateColors( leftHex, rightHex, t ).toLowerCase();
    this.palette.splice( idx, 0, { value } );
    this.positions.splice( idx, 0, pos );
    this.buildChannelStats();
    this.navigateTo( this.palette.map( p => p.value ), {} );
    this.analyticsService.logEvent( 'Gradient Stop Added', { count: this.palette.length } );
  }

  // Aggregate the palette into overall channel stats (RGB weight + average HSL /
  // HSV / CMYK), the same section the palette tool shows.
  private buildChannelStats(): void {
    // The analysis sections (Harmony, Tints/Shades, Temperature, Color Vision,
    // Matching Themes) rebuild whenever the palette does.
    this.buildAnalysis();
    const hexes = this.palette.map( p => p.value ).filter( Boolean );
    if ( hexes.length === 0 ) {
      this.channelStats = null;
      return;
    }
    let sumR = 0, sumG = 0, sumB = 0, sumS = 0, sumL = 0, hx = 0, hy = 0;
    let sumSv = 0, sumV = 0, sumC = 0, sumM = 0, sumY = 0, sumK = 0;
    for ( const hex of hexes ) {
      const num = parseInt( hex.replace( '#', '' ), 16 );
      const r = ( num >> 16 ) & 255;
      const g = ( num >> 8 ) & 255;
      const b = num & 255;
      sumR += r;
      sumG += g;
      sumB += b;
      const hsl = this.colorService.hexToHsl( hex.replace( '#', '' ) );
      sumS += hsl.s;
      sumL += hsl.l;
      const rad = ( hsl.h * Math.PI ) / 180;
      hx += Math.cos( rad );
      hy += Math.sin( rad );

      const rf = r / 255, gf = g / 255, bf = b / 255;
      const max = Math.max( rf, gf, bf ), min = Math.min( rf, gf, bf );
      sumV += max * 100;
      sumSv += ( max === 0 ? 0 : ( max - min ) / max ) * 100;
      const k = 1 - max;
      sumK += k * 100;
      if ( k < 1 ) {
        sumC += ( ( 1 - rf - k ) / ( 1 - k ) ) * 100;
        sumM += ( ( 1 - gf - k ) / ( 1 - k ) ) * 100;
        sumY += ( ( 1 - bf - k ) / ( 1 - k ) ) * 100;
      }
    }
    const n = hexes.length;
    const total = ( sumR + sumG + sumB ) || 1;
    let avgH = ( Math.atan2( hy / n, hx / n ) * 180 ) / Math.PI;
    if ( avgH < 0 ) { avgH += 360; }
    const avgS = sumS / n;
    const avgL = sumL / n;
    const avgSv = sumSv / n;
    const avgV = sumV / n;
    const avgC = sumC / n, avgM = sumM / n, avgYy = sumY / n, avgK = sumK / n;
    const weight = ( x: number ) => Math.round( ( x / total ) * 100 );
    this.channelStats = {
      rgb: [
        { label: 'R', width: weight( sumR ), text: weight( sumR ) + '%', color: '#e0584f' },
        { label: 'G', width: weight( sumG ), text: weight( sumG ) + '%', color: '#3fb27a' },
        { label: 'B', width: weight( sumB ), text: weight( sumB ) + '%', color: '#4a86e8' },
      ],
      hsl: [
        { label: 'H', width: ( avgH / 360 ) * 100, text: Math.round( avgH ) + '°', color: `hsl(${ avgH }, 75%, 55%)` },
        { label: 'S', width: avgS, text: Math.round( avgS ) + '%', color: `hsl(${ avgH }, ${ avgS }%, 55%)` },
        { label: 'L', width: avgL, text: Math.round( avgL ) + '%', color: `hsl(0, 0%, ${ avgL }%)` },
      ],
      hsv: [
        { label: 'H', width: ( avgH / 360 ) * 100, text: Math.round( avgH ) + '°', color: `hsl(${ avgH }, 75%, 55%)` },
        { label: 'S', width: avgSv, text: Math.round( avgSv ) + '%', color: `hsl(${ avgH }, ${ avgSv }%, 55%)` },
        { label: 'V', width: avgV, text: Math.round( avgV ) + '%', color: `hsl(${ avgH }, 40%, ${ Math.max( 20, avgV ) }%)` },
      ],
      cmyk: [
        { label: 'C', width: avgC, text: Math.round( avgC ) + '%', color: '#22b3c8' },
        { label: 'M', width: avgM, text: Math.round( avgM ) + '%', color: '#cf2e8a' },
        { label: 'Y', width: avgYy, text: Math.round( avgYy ) + '%', color: '#e6c100' },
        { label: 'K', width: avgK, text: Math.round( avgK ) + '%', color: '#3a3a3a' },
      ],
    };
  }

  // ===================== Analysis sections (ported from the palette tool) =====================

  // Run every analysis build (harmony, tints/shades, temperature, color vision,
  // matching themes) off the current palette.
  private buildAnalysis(): void {
    this.protanomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'protanomaly' );
    this.deuteranomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'deuteranomaly' );
    this.tritanomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'tritanomaly' );
    this.protanopiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'protanopia' );
    this.deuteranopiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'deuteranopia' );
    this.tritanopiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'tritanopia' );
    this.achromatomalyColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'achromatomaly' );
    this.achromatopsiaColors = this.colorService.generateColorBlindnessColorsForPalette( this.palette, 'achromatopsia' );
    this.harmonyResult = this.analyzePaletteHarmony();
    this.buildHarmonyScores();
    this.buildShadeTintTone();
    this.buildTemperature();
    this.buildRelatedThemes();
  }

  /** Valid #RRGGBB colors from the current palette, in order. */
  private pickHeroColors(): string[] {
    const out: string[] = [];
    for ( const item of this.palette || [] ) {
      const raw = item?.value;
      if ( typeof raw !== 'string' ) {
        continue;
      }
      const hex = raw.startsWith( '#' ) ? raw : '#' + raw;
      if ( /^#[0-9a-fA-F]{6}$/.test( hex ) ) {
        out.push( hex );
      }
    }
    return out;
  }

  public analyzePaletteHarmony() {
    const rgbPalette = this.palette.map( color => this.colorService.hexToRgb( color.value ) );
    const hsvPalette = rgbPalette.map( rgb => this.colorService.rgbToHsv( rgb ) );
    const hues = hsvPalette.map( hsv => hsv.h );

    let complementaryCount = 0;
    let analogousCount = 0;
    let triadicCount = 0;
    let warmCount = 0;
    let coolCount = 0;

    const huePairs = new Set();
    const hueTriplets = new Set();

    this.temperatures = [];
    this.analogous = [];
    this.complementaries = [];
    this.triadics = [];
    for ( let i = 0; i < hues.length; i++ ) {
      this.analogous.push( 0 );
      this.complementaries.push( 0 );
      this.triadics.push( 0 );
    }

    for ( let i = 0; i < hues.length; i++ ) {
      if ( this.colorService.isWarm( hues[ i ] ) ) {
        warmCount++;
        this.temperatures.push( 'warm' );
      } else if ( this.colorService.isCool( hues[ i ] ) ) {
        this.temperatures.push( 'cool' );
        coolCount++;
      }
      for ( let j = i + 1; j < hues.length; j++ ) {
        if ( !huePairs.has( `${ i },${ j }` ) && !huePairs.has( `${ j },${ i }` ) ) {
          if ( this.colorService.isComplementary( hues[ i ], hues[ j ] ) ) {
            this.complementaries[ i ] = this.complementaries[ i ] + 1;
            this.complementaries[ j ] = this.complementaries[ j ] + 1;
          }
          if ( this.colorService.isAnalogous( hues[ i ], hues[ j ] ) ) {
            this.analogous[ i ] = this.analogous[ i ] + 1;
            this.analogous[ j ] = this.analogous[ j ] + 1;
          }
          huePairs.add( `${ i },${ j }` );
        }
      }
    }

    for ( let i = 0; i < hues.length - 2; i++ ) {
      for ( let j = i + 1; j < hues.length - 1; j++ ) {
        for ( let k = j + 1; k < hues.length; k++ ) {
          const triplet = [ hues[ i ], hues[ j ], hues[ k ] ].sort().join( ',' );
          if ( !hueTriplets.has( triplet ) ) {
            if ( this.colorService.isTriadic( [ hues[ i ], hues[ j ], hues[ k ] ] ) ) {
              this.triadics[ i ] = this.triadics[ i ] + 1;
              this.triadics[ j ] = this.triadics[ j ] + 1;
              this.triadics[ k ] = this.triadics[ k ] + 1;
            }
            hueTriplets.add( triplet );
          }
        }
      }
    }

    for ( let i = 0; i < hues.length; i++ ) {
      analogousCount += this.analogous[ i ];
      complementaryCount += this.complementaries[ i ];
      triadicCount += this.triadics[ i ];
    }

    const temperatureCount = Math.max( warmCount, coolCount );
    let temperatureDecision = '';
    if ( warmCount > coolCount ) {
      temperatureDecision = 'warm';
    } else if ( warmCount < coolCount ) {
      temperatureDecision = 'cool';
    } else {
      temperatureDecision = 'equal';
    }

    return [
      { name: 'Analogous', count: analogousCount, max: ( this.palette.length * ( this.palette.length - 1 ) ) / 2 },
      { name: 'Complementary', count: complementaryCount, max: ( this.palette.length * ( this.palette.length - 1 ) ) / 2 },
      { name: 'Triadic', count: triadicCount, max: ( this.palette.length * ( this.palette.length - 1 ) * ( this.palette.length - 2 ) ) / 6 },
      { name: 'Temperature', count: temperatureCount, decision: temperatureDecision, max: this.palette.length },
    ];
  }

  private buildHarmonyScores(): void {
    const hexes = this.pickHeroColors();
    const n = hexes.length;
    const hues = hexes.map( h => this.colorService.hexToHsl( h.replace( '#', '' ) ).h );

    const dist = ( a: number, b: number ): number => {
      const d = Math.abs( a - b ) % 360;
      return d > 180 ? 360 - d : d;
    };
    const pair = ( ok: ( a: number, b: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          if ( ok( hues[ i ], hues[ j ] ) ) { counts[ i ]++; counts[ j ]++; }
        }
      }
      return counts;
    };
    const triple = ( ok: ( a: number, b: number, c: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          for ( let k = j + 1; k < n; k++ ) {
            if ( ok( hues[ i ], hues[ j ], hues[ k ] ) ) { counts[ i ]++; counts[ j ]++; counts[ k ]++; }
          }
        }
      }
      return counts;
    };
    const quad = ( ok: ( a: number, b: number, c: number, d: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          for ( let k = j + 1; k < n; k++ ) {
            for ( let l = k + 1; l < n; l++ ) {
              if ( ok( hues[ i ], hues[ j ], hues[ k ], hues[ l ] ) ) {
                counts[ i ]++; counts[ j ]++; counts[ k ]++; counts[ l ]++;
              }
            }
          }
        }
      }
      return counts;
    };

    const isComp = ( a: number, b: number ) => dist( a, b ) >= 156;
    // Monochrome and analogous kept DISJOINT (same hue vs neighbouring distinct hue)
    // so they no longer score the same.
    const isMono = ( a: number, b: number ) => dist( a, b ) <= 12;
    const isAnalog = ( a: number, b: number ) => dist( a, b ) > 12 && dist( a, b ) <= 40;
    const isTriad = ( a: number, b: number, c: number ) =>
      dist( a, b ) >= 90 && dist( a, b ) <= 150 &&
      dist( b, c ) >= 90 && dist( b, c ) <= 150 &&
      dist( a, c ) >= 90 && dist( a, c ) <= 150;
    const isSplit = ( a: number, b: number, c: number ) => {
      const tri = [ a, b, c ];
      for ( let base = 0; base < 3; base++ ) {
        const x = tri[ ( base + 1 ) % 3 ];
        const y = tri[ ( base + 2 ) % 3 ];
        if ( dist( tri[ base ], x ) >= 126 && dist( tri[ base ], x ) <= 174 &&
             dist( tri[ base ], y ) >= 126 && dist( tri[ base ], y ) <= 174 &&
             dist( x, y ) >= 36 && dist( x, y ) <= 90 ) {
          return true;
        }
      }
      return false;
    };
    const isTetra = ( a: number, b: number, c: number, d: number ) => {
      const pairings = [
        [ [ a, b ], [ c, d ] ],
        [ [ a, c ], [ b, d ] ],
        [ [ a, d ], [ b, c ] ],
      ];
      return pairings.some( p => isComp( p[ 0 ][ 0 ], p[ 0 ][ 1 ] ) && isComp( p[ 1 ][ 0 ], p[ 1 ][ 1 ] ) );
    };

    const chroma = hexes.map( h => {
      const v = parseInt( h.replace( '#', '' ), 16 );
      const r = ( v >> 16 ) & 255, g = ( v >> 8 ) & 255, b = v & 255;
      return Math.max( r, g, b ) - Math.min( r, g, b );
    } );
    const lowChroma = ( i: number ): boolean => chroma[ i ] <= 28;

    const pairIdx = ( ok: ( i: number, j: number ) => boolean ): number[] => {
      const counts = new Array( n ).fill( 0 );
      for ( let i = 0; i < n; i++ ) {
        for ( let j = i + 1; j < n; j++ ) {
          if ( ok( i, j ) ) { counts[ i ]++; counts[ j ]++; }
        }
      }
      return counts;
    };

    const schemes: { name: string; description: string; counts: number[] }[] = [
      { name: 'Analogous', description: 'Neighbouring hues. Calm, cohesive, easy on the eyes.', counts: pairIdx( ( i, j ) => !lowChroma( i ) && !lowChroma( j ) && isAnalog( hues[ i ], hues[ j ] ) ) },
      { name: 'Monochrome', description: 'One hue, varied in lightness. Quiet, clean, simple to balance.', counts: pairIdx( ( i, j ) => ( lowChroma( i ) && lowChroma( j ) ) || isMono( hues[ i ], hues[ j ] ) ) },
      { name: 'Complementary', description: 'The opposite hue. Maximum contrast, one bold accent.', counts: pair( isComp ) },
      { name: 'Split Complementary', description: 'The two hues beside the opposite. Softer tension, more range.', counts: triple( isSplit ) },
      { name: 'Triadic', description: 'Three evenly spaced hues. Balanced but lively.', counts: triple( isTriad ) },
      { name: 'Tetradic', description: 'Two complementary pairs. The richest scheme, hardest to balance.', counts: quad( isTetra ) },
    ];

    this.harmonyScores = schemes.map( s => ( {
      name: s.name,
      description: s.description,
      // The number of palette colours taking part in this scheme (a plain count,
      // not a percentage). Same measure across every tool and the theme page.
      score: s.counts.filter( c => c > 0 ).length,
      counts: s.counts,
    } ) );
  }

  private buildShadeTintTone(): void {
    const hexes = this.pickHeroColors();
    const EXTRA = 3;
    const rowsToward = ( endHex: string ): string[][] => {
      const ramps = hexes.map( base => this.colorService.generateShades( base, endHex, EXTRA + 1 ) );
      const rows: string[][] = [];
      for ( let step = 0; step <= EXTRA; step++ ) {
        rows.push( ramps.map( r => r[ step ] ) );
      }
      return rows;
    };
    this.shadeTintTone = [
      { title: 'Tints', rows: rowsToward( 'ffffff' ) },
      { title: 'Shades', rows: rowsToward( '000000' ) },
      { title: 'Tones', rows: rowsToward( '808080' ) },
    ];
  }

  private buildTemperature(): void {
    const hexes = this.pickHeroColors();
    if ( hexes.length === 0 ) {
      this.temperatureVariants = [];
      return;
    }
    const EXTRA = 3;
    const STRIDE = 2;
    const toRows = ( ramps: string[][] ): string[][] => {
      const rows: string[][] = [];
      for ( let step = 0; step <= EXTRA; step++ ) {
        rows.push( ramps.map( r => r[ step * STRIDE ] ) );
      }
      return rows;
    };
    const span = EXTRA * STRIDE + 1;
    this.temperatureVariants = [
      { title: 'Warmer', rows: toRows( hexes.map( h => this.colorService.generateWarmerColors( h, span ) ) ) },
      { title: 'Cooler', rows: toRows( hexes.map( h => this.colorService.generateCoolerColors( h, span ) ) ) },
    ];
  }

  private buildRelatedThemes(): void {
    const mine = this.pickHeroColors().map( h => this.colorService.hexToHsl( h.replace( '#', '' ) ) );
    if ( mine.length === 0 ) {
      this.relatedThemeList = [];
      return;
    }
    const dist = ( a: any, b: any ): number => {
      let dh = Math.abs( a.h - b.h );
      if ( dh > 180 ) { dh = 360 - dh; }
      const satW = Math.min( a.s, b.s ) / 100;
      const hueTerm = dh * satW;
      return Math.sqrt( hueTerm * hueTerm + 0.12 * ( a.s - b.s ) * ( a.s - b.s ) + 0.10 * ( a.l - b.l ) * ( a.l - b.l ) );
    };
    const scored: { product: Product; category: string; d: number }[] = [];
    for ( const line of THEMES ) {
      for ( const theme of line.products ) {
        const pal = ( theme.palette || [] )
          .filter( ( p: string ) => typeof p === 'string' && /^#?[0-9a-fA-F]{6}$/.test( p.replace( '#', '' ) ) )
          .map( ( p: string ) => this.colorService.hexToHsl( p.replace( '#', '' ) ) );
        if ( pal.length === 0 ) { continue; }
        let sum = 0;
        for ( const c of mine ) {
          let best = Infinity;
          for ( const t of pal ) {
            const d = dist( c, t );
            if ( d < best ) { best = d; }
          }
          sum += best;
        }
        scored.push( { product: theme, category: line.name, d: sum / mine.length } );
      }
    }
    scored.sort( ( a, b ) => a.d - b.d );
    const best = scored.length ? scored[ 0 ].d : 0;
    const MARGIN = 1;
    const close = scored.filter( ( s, i ) => i === 0 || s.d <= best + MARGIN );
    this.relatedThemeList = close.slice( 0, 3 ).map( ( { d, ...rest } ) => rest );
  }

  // Dot level from the share of the palette that takes part (count / total).
  harmonyLevel( count: number, total: number ): string {
    const frac = total > 0 ? count / total : 0;
    if ( frac >= 0.67 ) { return 'high'; }
    if ( frac >= 0.34 ) { return 'mid'; }
    return 'low';
  }

  // ----- Harmony as gradient bars (gradient tool: a single thin gradient bar per
  // scheme instead of discrete swatches) -----

  // The FULL-palette gradient, but every colour that does NOT take part in the
  // scheme (count <= 0) is greyed out — so the bar reads at a glance like the old
  // swatch row (lit vs muted), and reflects the score granularly instead of all or
  // nothing.
  harmonyBar( counts: number[] ): string {
    const cols = this.palette.map( ( p, i ) =>
      ( counts && counts[ i ] > 0 ) ? p.value : this.toGrey( p.value ) );
    return this.barFrom( cols );
  }

  // Same idea for Temperature: colours that don't match the dominant temperature
  // are greyed (unless the palette is temperature-balanced, when all stay lit).
  temperatureBar(): string {
    const decision = this.harmonyResult[ 3 ]?.decision;
    const cols = this.palette.map( ( p, i ) =>
      ( decision && decision !== 'equal' && this.temperatures[ i ] !== decision )
        ? this.toGrey( p.value )
        : p.value );
    return this.barFrom( cols );
  }

  // A gradient bar for a tints / shades / tones / temperature / color-vision row
  // (same look as the harmony bars): the row's colours spread left-to-right.
  shadeBar( row: string[] ): string {
    return this.barFrom( row || [] );
  }

  // The full palette as a left-to-right bar (the Color Vision "Original" row).
  originalBar(): string {
    return this.barFrom( this.palette.map( p => p.value ) );
  }

  // Horizontal position (%) of colour i along the bar — the same even spacing the
  // gradient uses — so the per-colour score labels sit right under their colour.
  harmonyStopLeft( i: number ): number {
    const n = this.palette.length;
    return n > 1 ? ( i / ( n - 1 ) ) * 100 : 50;
  }

  // Mobile font (px) for the per-colour analysis labels (hex codes / scores /
  // warm-cool). Picks the largest size, capped, at which a label still fits the
  // spacing between adjacent stops, so the row stays HORIZONTAL and never overlaps
  // however many colours the palette has. Read as a CSS var only under the phone
  // breakpoint (see .gr-harmony-label in the scss); desktop keeps its 12px.
  get analysisLabelFont(): number {
    const n = this.palette.length || 1;
    if ( n <= 1 ) { return 12; }
    // ~324px is about as wide as the bar gets on a phone.
    const spacing = 324 / ( n - 1 );
    // The end labels stay edge-aligned (so they never clip off the card), which
    // lets a label use at most ~2/3 of the gap to its neighbour before they touch.
    // A 7-char monospace hex is ~4.04em wide; size the font to honour that, capped
    // at the desktop 12px so few-colour palettes look exactly like before.
    const fit = ( ( 2 / 3 ) * spacing - 3 ) / 4.04;
    return Math.max( 6, Math.min( 12, Math.round( fit * 10 ) / 10 ) );
  }

  // A muted grey for a non-participating colour: its own luminance, washed toward
  // a light grey so it reads as "inactive" (matching the old grayscale swatch).
  private toGrey( hex: string ): string {
    const v = parseInt( ( hex || '' ).replace( '#', '' ), 16 );
    if ( isNaN( v ) ) { return '#c0c0c0'; }
    const r = ( v >> 16 ) & 255, g = ( v >> 8 ) & 255, b = v & 255;
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    const m = Math.round( lum * 0.5 + 192 * 0.5 );
    const h = m.toString( 16 ).padStart( 2, '0' );
    return `#${ h }${ h }${ h }`;
  }

  private barFrom( cols: string[] ): string {
    if ( cols.length === 0 ) { return 'transparent'; }
    if ( cols.length === 1 ) { return cols[ 0 ]; }
    const n = cols.length;
    const stops = cols.map( ( c, i ) => `${ c } ${ ( i / ( n - 1 ) * 100 ).toFixed( 2 ) }%` ).join( ', ' );
    return `linear-gradient(90deg, ${ stops })`;
  }

  colorVisionGroups(): { group: string; items: { name: string; note: string; colors: string[] }[] }[] {
    return [
      {
        group: 'Protan (red)',
        items: [
          { name: 'Protanomaly', note: 'Shifted L-cone (long-wavelength) sensitivity. Reds desaturate and darken.', colors: this.protanomalyColors },
          { name: 'Protanopia', note: 'No functional L-cones. Red and green hues are confused and reds appear dim.', colors: this.protanopiaColors },
        ],
      },
      {
        group: 'Deutan (green)',
        items: [
          { name: 'Deuteranomaly', note: 'Shifted M-cone (medium-wavelength) sensitivity. The most prevalent deficiency.', colors: this.deuteranomalyColors },
          { name: 'Deuteranopia', note: 'No functional M-cones. Red and green hues are confused at normal luminance.', colors: this.deuteranopiaColors },
        ],
      },
      {
        group: 'Tritan (blue)',
        items: [
          { name: 'Tritanomaly', note: 'Shifted S-cone (short-wavelength) sensitivity. Blue and green converge.', colors: this.tritanomalyColors },
          { name: 'Tritanopia', note: 'No functional S-cones. Blue and yellow hues are confused.', colors: this.tritanopiaColors },
        ],
      },
      {
        group: 'Monochromacy (no color)',
        items: [
          { name: 'Achromatomaly', note: 'Reduced function across all three cone types, yielding a heavily desaturated image.', colors: this.achromatomalyColors },
          { name: 'Achromatopsia', note: 'No functional cones. Perception reduces to luminance, seen as grayscale.', colors: this.achromatopsiaColors },
        ],
      },
    ];
  }

  copyText( text: string, key: string ): void {
    this.clipboard.copy( text );
    this.copiedKey = key;
    if ( this.copiedKeyTimer ) { clearTimeout( this.copiedKeyTimer ); }
    this.copiedKeyTimer = setTimeout( () => ( this.copiedKey = '' ), 1200 );
  }

  openRouteInNewTab( route: string ): void {
    const url = this.router.serializeUrl( this.router.createUrlTree( [ route ] ) );
    window.open( url, '_blank' );
  }

  // ----- Gradient render -----

  gradient(): string {
    return this.gradientOf( this.palette.map( p => p.value ) );
  }

  /** the gradient as it is set (type, angle, stops) over other colours: the Tints, Temperature and Color
   *  Vision cards draw the gradient itself, as the wallpaper tool draws its picture (owner 2026-10-12) */
  gradientOf( list: string[] ): string {
    const colors = ( list || [] ).filter( Boolean );
    if ( colors.length === 0 ) {
      return 'transparent';
    }
    if ( colors.length === 1 ) {
      return colors[ 0 ];
    }
    const n = colors.length;
    const usePos = this.positions.length === n;
    const stops = colors.map( ( c, i ) => {
      const pct = usePos ? this.positions[ i ] : ( i / ( n - 1 ) * 100 );
      return `${ c } ${ pct.toFixed( 2 ) }%`;
    } ).join( ', ' );
    if ( this.type === 'radial' ) {
      return `radial-gradient(circle, ${ stops })`;
    }
    if ( this.type === 'conic' ) {
      return `conic-gradient(from ${ this.angle }deg, ${ stops })`;
    }
    return `linear-gradient(${ this.angle }deg, ${ stops })`;
  }

  /** a Tints, Temperature card: the generator takes its colours (as on the wallpaper tool) */
  usePalette( colors: string[] ): void {
    if ( !colors?.length ) {
      return;
    }
    this.palette = colors.map( value => ( { value } ) );
    this.navigateTo( colors, {} );
  }

  cssDeclaration(): string {
    return `background: ${ this.gradient() };`;
  }

  // The hero backdrop: when an image was uploaded, show that image (sharp, via the
  // cp-hero-noblur class) instead of the live gradient, mirroring the palette tool.
  get heroBgValue(): string {
    if ( this.uploadedImg?.src ) {
      return `url(${ this.uploadedImg.src }) center / cover`;
    }
    return this.gradient();
  }

  copyCss(): void {
    const text = this.cssRule();
    const done = () => {
      this.copied = true;
      if ( this.copiedTimer ) {
        clearTimeout( this.copiedTimer );
      }
      this.copiedTimer = setTimeout( () => ( this.copied = false ), 1400 );
      this.analyticsService.logEvent( 'Gradient Copied', { type: this.type } );
    };
    if ( navigator.clipboard && navigator.clipboard.writeText ) {
      navigator.clipboard.writeText( text ).then( done, () => this.legacyCopy( text, done ) );
    } else {
      this.legacyCopy( text, done );
    }
  }

  private legacyCopy( text: string, done: () => void ): void {
    const ta = document.createElement( 'textarea' );
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild( ta );
    ta.select();
    try {
      document.execCommand( 'copy' );
      done();
    } finally {
      document.body.removeChild( ta );
    }
  }

  // ----- Sidebar scroll-spy -----

  scrollToSection( key: string ): void {
    const scroller = ( document.body.scrollHeight > document.body.clientHeight + 4 )
      ? document.body
      : document.documentElement;

    if ( key === 'top' ) {
      this.activeSection = 'top';
      this.scrollActivePillToStart();
      this.tweenScroll( scroller, 0 );
      return;
    }

    const el = document.getElementById( 'sec-' + key );
    if ( !el ) {
      return;
    }
    this.activeSection = key;
    this.scrollActivePillToStart();

    // clear the nav and the sticky chips' bar under it (owner 2026-10-12)
    let headerOffset = 84;
    const bar = document.querySelector( '.cp-chipbar' ) as HTMLElement | null;
    if ( bar ) {
      const stickyTop = parseFloat( getComputedStyle( bar ).top ) || 72;
      headerOffset = stickyTop + bar.offsetHeight + 16;
    }
    const target = Math.max( 0, el.getBoundingClientRect().top + scroller.scrollTop - headerOffset );
    this.tweenScroll( scroller, target );
  }

  private scrollActivePillToStart(): void {
    setTimeout( () => {
      const strip = document.querySelector( '.cp-chipbar-row' ) as HTMLElement | null;
      if ( !strip || strip.scrollWidth <= strip.clientWidth ) {
        return;
      }
      const active = strip.querySelector( '.pl-chip.on' ) as HTMLElement | null;
      if ( !active ) {
        return;
      }
      const sr = strip.getBoundingClientRect();
      const ar = active.getBoundingClientRect();
      const target = strip.scrollLeft + ( ar.left - sr.left ) - ( sr.width - ar.width ) / 2;
      strip.scrollTo( { left: Math.max( 0, target ), behavior: 'smooth' } );
    } );
  }

  private tweenScroll( scroller: HTMLElement, target: number ): void {
    const startTop = scroller.scrollTop;
    const duration = 420;
    const start = performance.now();
    const ease = ( t: number ) => 1 - Math.pow( 1 - t, 3 );
    const step = ( now: number ) => {
      const p = Math.min( 1, ( now - start ) / duration );
      scroller.scrollTop = startTop + ( target - startTop ) * ease( p );
      if ( p < 1 ) {
        requestAnimationFrame( step );
      }
    };
    requestAnimationFrame( step );
  }
}
